import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ArrowRight, Eye, EyeOff, LoaderCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import PhoneInput from '@/components/PhoneInput';
import { getDefaultCountryByBrowserLanguage } from '@/constants/countries';
import { useAuth } from '@/contexts/AuthContext';
import { useCampaignEntryCopy } from '@/i18n/campaignEntry';
import { registerUser } from '@/lib/auth/registerUser';
import { normalizeRegisterError } from '@/lib/auth/normalizeRegisterError';
import { getGaClientId } from '@/lib/analytics/gaClientId';
import { trackEvent } from '@/lib/analytics/trackEvent';
import { SUPER_HERO_ENTRY_PATH } from '@/lib/campaigns/superHeroCampaign';
import { getUtmParams } from '@/lib/utm-params';

export function SuperHeroAuthForm() {
  const copy = useCampaignEntryCopy();
  const { i18n } = useTranslation();
  const { login, user } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [fields, setFields] = useState({ email: '', name: '', phone: '', country: getDefaultCountryByBrowserLanguage(), password: '', confirmation: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [marketing, setMarketing] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<keyof typeof copy | null>(null);
  const pendingRef = useRef(false);
  const mounted = useRef(false);
  const currentUser = useRef(user?.id);
  currentUser.current = user?.id;
  const signupStarted = useRef(false);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const update = (field: keyof typeof fields, value: string) => {
    setFields(previous => ({ ...previous, [field]: value }));
    setError(null);
    if (mode === 'register' && !signupStarted.current) {
      signupStarted.current = true;
      trackEvent('sign_up_started');
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (pendingRef.current) return;
    if (mode === 'register' && fields.password !== fields.confirmation) { setError('passwordMismatch'); return; }
    pendingRef.current = true;
    setPending(true);
    setError(null);
    const startingUser = currentUser.current;
    let registering = mode === 'register';

    try {
      if (registering) {
        await registerUser({
          email: fields.email.trim(),
          name: fields.name.trim(),
          phone: fields.phone,
          country: fields.country,
          password: fields.password,
          locale: (i18n?.resolvedLanguage || i18n?.language || 'pt').startsWith('en') ? 'en' : 'pt',
          emailMarketingOptIn: marketing,
          utmParams: getUtmParams(),
          gaClientId: getGaClientId(),
        });
        if (!mounted.current || currentUser.current !== startingUser) return;
        registering = false;
      }
      await login(fields.email.trim(), fields.password);
      // The page waits for the current authenticated hook state before entry.
    } catch (cause: unknown) {
      if (!mounted.current || currentUser.current !== startingUser) return;
      const code = registering ? normalizeRegisterError(cause).code : null;
      setError(code === 'emailTaken' ? 'emailTaken' : code === 'invalidEmail' ? 'invalidEmail' : registering ? 'registerFailed' : 'loginFailed');
    } finally {
      pendingRef.current = false;
      if (mounted.current) setPending(false);
    }
  };

  return <>
    <span className="super-hero-eyebrow">{copy.accountStep}</span>
    <h2 className="super-hero-form-title">{mode === 'login' ? copy.loginTitle : copy.registerTitle}</h2>
    <p className="super-hero-form-description">{mode === 'login' ? copy.loginDescription : copy.registerDescription}</p>

    <div className="super-hero-mode-switch" aria-label={copy.accountStep}>
      {(['login', 'register'] as const).map(value => <Button key={value} type="button" variant="ghost" aria-pressed={mode === value} disabled={pending} onClick={() => { setMode(value); setError(null); }}>
        {value === 'login' ? copy.loginMode : copy.registerMode}
      </Button>)}
    </div>

    <form onSubmit={submit} aria-label={mode === 'login' ? copy.loginMode : copy.registerMode} aria-busy={pending}>
      <fieldset disabled={pending} className="super-hero-fields">
        <GoogleSignInButton redirect={SUPER_HERO_ENTRY_PATH} label={copy.google} className="super-hero-google" />
        <div className="super-hero-divider"><span>{copy.orEmail}</span></div>
        {error && <p role="alert" className="super-hero-error">{copy[error]}</p>}
        <div className="super-hero-field">
          <Label htmlFor="campaign-email">{copy.email}</Label>
          <Input id="campaign-email" name="email" type="email" autoComplete="email" placeholder={copy.emailPlaceholder} required value={fields.email} onChange={event => update('email', event.target.value)} />
        </div>
        {mode === 'register' && <>
          <div className="super-hero-field">
            <Label htmlFor="campaign-name">{copy.name}</Label>
            <Input id="campaign-name" name="name" autoComplete="name" required value={fields.name} onChange={event => update('name', event.target.value)} />
          </div>
          <PhoneInput value={fields.phone} onChange={value => update('phone', value)} country={fields.country} onCountryChange={value => update('country', value)} label={copy.phone} className="super-hero-phone" required />
        </>}
        <div className="super-hero-field">
          <Label htmlFor="campaign-password">{copy.password}</Label>
          <div className="super-hero-password">
            <Input id="campaign-password" name="password" type={showPassword ? 'text' : 'password'} autoComplete={mode === 'register' ? 'new-password' : 'current-password'} minLength={mode === 'register' ? 6 : undefined} required value={fields.password} onChange={event => update('password', event.target.value)} />
            <Button type="button" variant="ghost" size="icon" aria-label={showPassword ? copy.hidePassword : copy.showPassword} aria-pressed={showPassword} onClick={() => setShowPassword(previous => !previous)}>
              {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </Button>
          </div>
        </div>
        {mode === 'register' ? <>
          <div className="super-hero-field">
            <Label htmlFor="campaign-confirm-password">{copy.confirmPassword}</Label>
            <div className="super-hero-password">
              <Input id="campaign-confirm-password" name="passwordConfirmation" type={showConfirmation ? 'text' : 'password'} autoComplete="new-password" minLength={6} required value={fields.confirmation} onChange={event => update('confirmation', event.target.value)} />
              <Button type="button" variant="ghost" size="icon" aria-label={showConfirmation ? copy.hideConfirmation : copy.showConfirmation} aria-pressed={showConfirmation} onClick={() => setShowConfirmation(previous => !previous)}>
                {showConfirmation ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
              </Button>
            </div>
          </div>
          <div className="super-hero-marketing">
            <Checkbox id="campaign-marketing" checked={marketing} onCheckedChange={value => setMarketing(value === true)} />
            <Label htmlFor="campaign-marketing">{copy.marketing}</Label>
          </div>
        </> : <Link className="super-hero-forgot" to={`/forgot-password?redirect=${encodeURIComponent(SUPER_HERO_ENTRY_PATH)}`}>{copy.forgotPassword}</Link>}

        <Button type="submit" disabled={pending} className="super-hero-primary">
          {pending ? <><LoaderCircle className="animate-spin" aria-hidden="true" />{copy.submitting}</> : <>{mode === 'register' ? copy.registerSubmit : copy.loginSubmit}<ArrowRight aria-hidden="true" /></>}
        </Button>
        {mode === 'register' && <p className="super-hero-terms">
          {copy.tosBefore}<Link to="/terms" target="_blank" rel="noopener noreferrer">{copy.terms}</Link>{copy.tosBetween}<Link to="/privacy" target="_blank" rel="noopener noreferrer">{copy.privacy}</Link>{copy.tosAfter}
        </p>}
      </fieldset>
    </form>
  </>;
}
